import { all, put, select, takeLatest, call, take } from 'redux-saga/effects';
import { handleError } from 'spw-redux/modules/UI/messages/actions';
import {
  currentFloor,
  currentProject as currentProjectSelector,
  projectSpaceTypes as selectProjectSpaceTypes,
} from 'spw-redux/modules/projects/selectors';
import {
  updateProjectSpaceType as updateProjectSpaceTypeApi,
  SpaceType as spaceTypeApi,
  Project,
} from 'spw-api';
import { IProjectSpaceType } from 'spw-redux/modules/space_types/types';
import { getDefaultSpaceTypeCategory } from 'spw-redux/modules/project-space-type-categories/utils';
import { APIResponse } from 'spw-redux/types';
import { isNil } from 'ramda';
import {
  actions,
  BULK_UPDATE_PROJECT_SPACE_TYPES,
  FETCH_PROJECT_SPACE_TYPES,
  FETCH_PROJECT_SPACE_TYPES_SUCCESS,
  REASSIGN_PROJECT_SPACE_TYPE,
  UPDATE_PROJECT_SPACE_TYPE,
  UPDATE_PROJECT_SPACE_TYPE_POSITION,
} from './actions';
import { fetchCurrentWorksheet } from '../calculators/actions';
import { IFloor, IProject } from '../projects/types';
import { getSpaces, setSpaceToUpdate } from '../floorplan/actions';
import { selectSpaceToUpdate } from '../floorplan/selectors';
import { ISpace } from '../floorplan/types';

function* fetchProjectSpaceTypes(
  _action: ReturnType<typeof actions.fetchProjectSpaceTypes>
) {
  try {
    const project: ReturnType<typeof currentProjectSelector> = yield select(
      currentProjectSelector
    );
    if (project) {
      const {
        data: { spaceTypes },
      } = yield call(Project.getProjectSpaceTypes, project.id);

      const projectSpaceTypes = spaceTypes.map(
        (projectSpaceType: IProjectSpaceType) => {
          return {
            ...projectSpaceType,
            category: {
              ...projectSpaceType.category,
              defaultSpaceTypeCategory: getDefaultSpaceTypeCategory(
                projectSpaceType.category.defaultSpaceTypeCategory
              ),
            },
          };
        }
      );

      yield put(actions.fetchProjectSpaceTypesSuccess(projectSpaceTypes));
    }
  } catch (e) {
    yield put(handleError(e));
  }
}

function* updateProjectSpaceType(
  action: ReturnType<typeof actions.updateProjectSpaceType>
) {
  try {
    const {
      projectSpaceTypeId,
      categoryId,
      spaceTypeId,
      icon,
      clientFacingName,
    } = action.payload;
    const project: IProject = yield select(currentProjectSelector);
    yield call(updateProjectSpaceTypeApi, {
      id: projectSpaceTypeId || undefined,
      categoryId,
      projectId: project.id,
      spaceTypeId,
      icon,
      clientFacingName,
    });
    yield put(actions.fetchProjectSpaceTypes());
    yield take(FETCH_PROJECT_SPACE_TYPES_SUCCESS);

    if (project.type === 'programming') {
      yield put(fetchCurrentWorksheet());
    }

    if (project.type === 'existing_site_analysis') {
      const spaceToUpdate: ISpace = yield select(selectSpaceToUpdate);

      if (
        spaceToUpdate?.projectSpaceType.id === projectSpaceTypeId &&
        clientFacingName
      ) {
        yield put(
          setSpaceToUpdate({
            ...spaceToUpdate,
            projectSpaceType: {
              ...spaceToUpdate.projectSpaceType,
              clientFacingName,
            },
          })
        );
      }
    }
  } catch (e) {
    yield put(handleError(e));
  }
}

function* updateSpaceTypes(projectSpaceTypes: IProjectSpaceType[]) {
  const projectSpaceTypesToUpdate = projectSpaceTypes.map((pst) => ({
    id: pst.id,
    previousId: pst.previousId,
    categoryId: (pst?.category?.id || pst?.category) as number,
  }));

  yield call(spaceTypeApi.updateProjectSpaceTypes, projectSpaceTypesToUpdate);

  yield put(actions.fetchProjectSpaceTypes());
  yield take(FETCH_PROJECT_SPACE_TYPES_SUCCESS);
}

function* reassignProjectSpaceTypeCall(
  action: ReturnType<typeof actions.reassignProjectSpaceType>
) {
  try {
    const { projectSpaceTypeId, categoryId } = action.payload;
    yield call(
      spaceTypeApi.reassignProjectSpaceType,
      projectSpaceTypeId,
      categoryId
    );
    yield put(actions.fetchProjectSpaceTypes());
    yield take(FETCH_PROJECT_SPACE_TYPES_SUCCESS);

    const floor: IFloor = yield select(currentFloor);
    yield put(getSpaces(floor.id));
  } catch (e) {
    yield put(handleError(e));
  }
}

function* bulkUpdateProjectSpaceTypes(
  action: ReturnType<typeof actions.bulkUpdateProjectSpaceTypes>
) {
  try {
    const { projectSpaceTypes } = action.payload;

    yield call(updateSpaceTypes, projectSpaceTypes);
    yield put(fetchCurrentWorksheet());
  } catch (e) {
    yield put(handleError(e));
  }
}

function* updateProjectSpaceTypePositionCall(
  action: ReturnType<typeof actions.updateProjectSpaceTypePosition>
) {
  try {
    const { projectSpaceTypeId, previousId } = action.payload;

    const {
      data: { projectSpaceType: updatedProjectSpaceType },
    }: APIResponse<typeof spaceTypeApi.updateProjectSpaceTypePosition> = yield call(
      spaceTypeApi.updateProjectSpaceTypePosition,
      projectSpaceTypeId,
      previousId
    );

    let projectSpaceTypes: IProjectSpaceType[] = yield select(
      selectProjectSpaceTypes
    );
    projectSpaceTypes = projectSpaceTypes.slice();

    const originalPosition = projectSpaceTypes.findIndex(
      (projectSpaceType: IProjectSpaceType) =>
        projectSpaceType.id === projectSpaceTypeId
    );

    const positionAfterOriginal = projectSpaceTypes.findIndex(
      (projectSpaceType: IProjectSpaceType) =>
        projectSpaceType.previousId === projectSpaceTypeId
    );

    const targetPosition = projectSpaceTypes.findIndex(
      (projectSpaceType: IProjectSpaceType) =>
        projectSpaceType.previousId === previousId
    );

    // Pst in target position is not the last item of the list
    if (targetPosition > -1) {
      projectSpaceTypes[targetPosition].previousId = updatedProjectSpaceType.id;
    }

    if (positionAfterOriginal > -1) {
      projectSpaceTypes[positionAfterOriginal].previousId =
        projectSpaceTypes[originalPosition].previousId;
    }

    // Removes project space type in the old position
    const [projectSpaceType] = projectSpaceTypes.splice(originalPosition, 1);
    const newProjectSpaceType = {
      ...projectSpaceType,
      previousId: updatedProjectSpaceType.previousId,
    };

    if (isNil(updatedProjectSpaceType.previousId)) {
      const positionInCategory = projectSpaceTypes.findIndex(
        (pst: IProjectSpaceType) =>
          pst.category.id === newProjectSpaceType.category.id
      );

      projectSpaceTypes.splice(positionInCategory, 0, newProjectSpaceType);
    } else if (targetPosition === -1) {
      const lastItemPosition = projectSpaceTypes.findIndex(
        (pst: IProjectSpaceType) =>
          pst.id === updatedProjectSpaceType.previousId
      );

      projectSpaceTypes.splice(lastItemPosition + 1, 0, newProjectSpaceType);
    } else if (targetPosition > originalPosition) {
      projectSpaceTypes.splice(targetPosition - 1, 0, newProjectSpaceType);
    } else {
      projectSpaceTypes.splice(targetPosition, 0, newProjectSpaceType);
    }

    yield put(actions.fetchProjectSpaceTypesSuccess(projectSpaceTypes));
  } catch (e) {
    yield put(handleError(e));
  }
}

function* saga() {
  yield all([
    takeLatest(FETCH_PROJECT_SPACE_TYPES, fetchProjectSpaceTypes),
    takeLatest(UPDATE_PROJECT_SPACE_TYPE, updateProjectSpaceType),
    takeLatest(BULK_UPDATE_PROJECT_SPACE_TYPES, bulkUpdateProjectSpaceTypes),
    takeLatest(
      UPDATE_PROJECT_SPACE_TYPE_POSITION,
      updateProjectSpaceTypePositionCall
    ),
    takeLatest(REASSIGN_PROJECT_SPACE_TYPE, reassignProjectSpaceTypeCall),
  ]);
}

export default saga;
